import { useState } from "react";
import { api, type Role, type User } from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

const ROLES: Role[] = [
  "viewer",
  "analyst",
  "strategy_manager",
  "risk_manager",
  "operator",
  "owner",
];

export function Users({
  onSignedOut,
  currentUserId,
}: {
  onSignedOut: () => void;
  currentUserId: string;
}) {
  const { data, error, loading, reload } = useApiData(() => api.users(), onSignedOut);
  const [actionError, setActionError] = useState<string | null>(null);

  async function run(work: () => Promise<unknown>) {
    setActionError(null);
    try {
      await work();
      reload();
    } catch (err) {
      // Server-side refusals land here — most importantly
      // LAST_OWNER_PROTECTED, which the UI must not pretend to handle by
      // hiding the control, since the rule is enforced server-side anyway.
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <h1>Users</h1>
      <p className="subtitle">
        Roles follow docs/07 §3. Changing a role or deactivating an account signs that
        person out immediately.
      </p>

      {actionError && <div className="notice error">{actionError}</div>}

      <NewUser onCreate={(u, p, r) => run(() => api.createUser(u, p, r))} />

      <PageState loading={loading} error={error}>
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.users.map((user) => (
                <UserRow
                  key={user.user_id}
                  user={user}
                  isSelf={user.user_id === currentUserId}
                  onChange={(changes) => run(() => api.updateUser(user.user_id, changes))}
                  onResetPassword={(password) =>
                    run(() => api.resetUserPassword(user.user_id, password))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </PageState>
    </>
  );
}

function UserRow({
  user,
  isSelf,
  onChange,
  onResetPassword,
}: {
  user: User;
  isSelf: boolean;
  onChange: (changes: { role?: Role; is_active?: boolean }) => void;
  onResetPassword: (password: string) => void;
}) {
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  return (
    <tr>
      <td>
        {user.username}
        {isSelf && <span className="dim"> (you)</span>}
      </td>
      <td>
        <select value={user.role} onChange={(e) => onChange({ role: e.target.value as Role })}>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </td>
      <td>
        <span className={`badge ${user.is_active ? "ok" : "danger"}`}>
          {user.is_active ? "active" : "deactivated"}
        </span>
      </td>
      <td className="dim">{formatTime(user.created_at)}</td>
      <td>
        <button className="link" onClick={() => onChange({ is_active: !user.is_active })}>
          {user.is_active ? "Deactivate" : "Reactivate"}
        </button>
        {" · "}
        <button className="link" onClick={() => setResetting((open) => !open)}>
          Reset password
        </button>
        {resetting && (
          <form
            style={{ marginTop: 8, maxWidth: 260 }}
            onSubmit={(e) => {
              e.preventDefault();
              onResetPassword(newPassword);
              setNewPassword("");
              setResetting(false);
            }}
          >
            <label>
              New password for {user.username}
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
              />
              {/* A reset exists for when control of an account is in doubt,
                  so it ends every session that user has open. */}
              <span className="dim">Signs them out on every device.</span>
            </label>
            <button className="primary" type="submit">
              Reset
            </button>
          </form>
        )}
      </td>
    </tr>
  );
}

function NewUser({
  onCreate,
}: {
  onCreate: (username: string, password: string, role: Role) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("viewer");

  return (
    <details className="card">
      <summary>Add a user</summary>
      <form
        style={{ marginTop: 14, maxWidth: 340 }}
        onSubmit={(e) => {
          e.preventDefault();
          onCreate(username, password, role);
          setUsername("");
          setPassword("");
        }}
      >
        <label>
          Username
          <input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
          <span className="dim">At least 12 characters. A passphrase is fine.</span>
        </label>
        <label>
          Role
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" type="submit">
          Create user
        </button>
      </form>
    </details>
  );
}
